import { NavLink } from "react-router-dom";
import Box from "/app/src/components/generic/components/box";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import { useCallback } from "react";
import { Chip } from "@heroui/react";

type LinkItem = {
  path: string;
  label: string;
  count: number | string;
  icon?: string;
};

/**
 * Renders a card component for a given link with hover interaction.
 *
 * @param {LinkItem} link - The link item containing path, label, and count.
 * @param {string|null} hovered - The current hovered link path or null if none.
 * @param {(s: string|null) => void} setHovered - Callback to set the hovered link path.
 * @returns {JSX.Element} The UI representation of the ModelCard.
 */

export default function ModelCard({
  link,
  hovered,
  setHovered,
}: {
  link: LinkItem;
  hovered: string | null;
  setHovered: (s: string | null) => void;
}) {
  const handleMouseEnter = useCallback(
    () => setHovered(link.path),
    [link.path, setHovered],
  );
  const handleMouseLeave = useCallback(() => setHovered(null), [setHovered]);

  return (
    <NavLink
      to={link.path}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box className="!mb-0 h-[100px] shadow-md !rounded-xl flex items-center justify-between hover:border-primary-surface hover:bg-primary-muted transition-all duration-300">
        <div className="flex items-center">
          <IconBuilder
            className="mr-2 transition-colors duration-300"
            size={28}
            icon={link.icon ?? "ExplorerIcon"}
            color={hovered === link.path ? "#2563EB" : "#202023"}
          />
          <h3
            className={`text-[18px] text-primary-default mb-0 transition-colors duration-300 ${
              hovered === link.path ? "text-primary-surface" : "text-black"
            }`}
          >
            {link.label}
          </h3>
        </div>
        <Chip
          color={hovered === link.path ? "primary" : "default"}
          className="transition-colors duration-300"
          size="sm"
        >
          {link.count}
        </Chip>
      </Box>
    </NavLink>
  );
}
